import React, { useEffect, useState } from 'react';
import {
  StyleSheet,
  ScrollView,
  Text,
  ActivityIndicator,
  View,
} from 'react-native';
import NotificationSettingRow from '../NotificationSettingRow';
import Colors from '../../constants/Colors';
import Button from '../Button';
import updateNotificationPreferences from '../../requests/Users/UpdateNotificationPreferences';
import AlertModal from '../AlertModal';
import Style from '../../constants/Style';
import {
  HappyScoreCategory,
  HappyScoreCategoryTitles,
  NotificationDeliveryChannel,
  StaffTicketEmailNotificationEvents,
  StaffTicketNotificationEventTitles,
  StaffTicketNotificationEventType,
  StaffTicketNotificationPreferences,
  StaffTicketPushNotificationEvents,
  UserNotificationPreferences,
} from '../../constants/Types';
import {
  NOTIFICATION_PREFERENCES_LOAD_FAILURE,
  NOTIFICATION_PREFERENCES_SAVE_FAILURE,
  NOTIFICATION_PREFERENCES_SAVE_SUCCESS,
} from '../../constants/AlertMessages';

interface NotificationPreferencesProps {
  showBuildingNotifications: boolean;
  buildingNotificationPreferences?: UserNotificationPreferences;
  ticketNotificationPreferences?: StaffTicketNotificationPreferences;
  preferencesLoaded: boolean;
  retryPreferences: () => void;
}

const BUILDING_NOTIFICATION_CATEGORIES = Object.values(HappyScoreCategory);
type BuildingNotificationPreferences = UserNotificationPreferences['enabledCategories'];

function hydrateBuildingPreferences(
  preferences?: BuildingNotificationPreferences,
): BuildingNotificationPreferences {
  return Object.fromEntries(
    BUILDING_NOTIFICATION_CATEGORIES.map((category) => [
      category,
      preferences?.[category] ?? true,
    ]),
  ) as BuildingNotificationPreferences;
}

function hydrateTicketPreferences(
  preferences?: StaffTicketNotificationPreferences,
): StaffTicketNotificationPreferences {
  return {
    [NotificationDeliveryChannel.EMAIL]: Object.fromEntries(
      StaffTicketEmailNotificationEvents.map((eventType) => [
        eventType,
        preferences?.[NotificationDeliveryChannel.EMAIL]?.[eventType] ?? true,
      ]),
    ) as StaffTicketNotificationPreferences[NotificationDeliveryChannel.EMAIL],
    [NotificationDeliveryChannel.PUSH]: Object.fromEntries(
      StaffTicketPushNotificationEvents.map((eventType) => [
        eventType,
        preferences?.[NotificationDeliveryChannel.PUSH]?.[eventType] ?? true,
      ]),
    ) as StaffTicketNotificationPreferences[NotificationDeliveryChannel.PUSH],
  };
}

const NotificationPreferences = ({
  showBuildingNotifications,
  buildingNotificationPreferences,
  ticketNotificationPreferences,
  preferencesLoaded,
  retryPreferences,
}: NotificationPreferencesProps) => {
  const [draftBuilding, setDraftBuilding] = useState<BuildingNotificationPreferences>();
  const [confirmedBuilding, setConfirmedBuilding] = useState<BuildingNotificationPreferences>();
  const [draftTicket, setDraftTicket] = useState<StaffTicketNotificationPreferences>();
  const [confirmedTicket, setConfirmedTicket] = useState<StaffTicketNotificationPreferences>();
  const [loading, setLoading] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);

  useEffect(() => {
    if (preferencesLoaded !== true) return;

    const buildingPreferences = hydrateBuildingPreferences(
      buildingNotificationPreferences?.enabledCategories,
    );
    const ticketPreferences = hydrateTicketPreferences(
      ticketNotificationPreferences,
    );
    setDraftBuilding(buildingPreferences);
    setConfirmedBuilding(buildingPreferences);
    setDraftTicket(ticketPreferences);
    setConfirmedTicket(ticketPreferences);
  }, [
    buildingNotificationPreferences,
    preferencesLoaded,
    ticketNotificationPreferences,
  ]);

  useEffect(() => {
    if (preferencesLoaded !== false) return;
    setDraftBuilding(confirmedBuilding);
    setDraftTicket(confirmedTicket);
  }, [confirmedBuilding, confirmedTicket, preferencesLoaded]);

  const showAlert = (title: string, message: string, error: boolean = true) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setLoading(false);
    setAlertVisible(true);
  };

  const save = () => {
    if (loading || !draftTicket) return;
    setLoading(true);

    updateNotificationPreferences({
      ...(showBuildingNotifications && draftBuilding
        ? { enabledCategories: draftBuilding }
        : {}),
      ticketNotificationPreferences: draftTicket,
    })
      .then((res) => {
        if (res?.message) {
          setConfirmedBuilding(draftBuilding);
          setConfirmedTicket(draftTicket);
          showAlert(
            'Notification preferences saved successfully',
            NOTIFICATION_PREFERENCES_SAVE_SUCCESS,
            false,
          );
        } else {
          setDraftBuilding(confirmedBuilding);
          setDraftTicket(confirmedTicket);
          showAlert(
            'Error',
            res?.errorText ?? NOTIFICATION_PREFERENCES_SAVE_FAILURE,
          );
        }
      })
      .catch((error) => {
        setDraftBuilding(confirmedBuilding);
        setDraftTicket(confirmedTicket);
        showAlert(
          'Error',
          error?.message ?? NOTIFICATION_PREFERENCES_SAVE_FAILURE,
        );
      });
  };

  const updateTicketPreference = (
    channel: NotificationDeliveryChannel,
    eventType: StaffTicketNotificationEventType,
    enabled: boolean,
  ) => setDraftTicket(
    (current) => ({
      ...current,
      [channel]: {
        ...current[channel],
        [eventType]: enabled,
      },
    } as StaffTicketNotificationPreferences),
  );

  if (preferencesLoaded === null) {
    return (
      <ActivityIndicator
        animating
        size="large"
        style={Style.activityIndicator}
      />
    );
  }

  const hasConfirmedPreferences = !!draftTicket;
  const controlsDisabled = loading || preferencesLoaded !== true;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      testID="notifications-list"
    >
      {preferencesLoaded === false && (
        <View style={styles.loadErrorContainer}>
          <Text style={styles.errorText}>
            {NOTIFICATION_PREFERENCES_LOAD_FAILURE}
          </Text>
          <Button
            title="Retry"
            onPress={retryPreferences}
            disabled={loading}
            style={styles.retryButton}
          />
        </View>
      )}
      {hasConfirmedPreferences && (
        <>
          {showBuildingNotifications && draftBuilding && (
            <>
              <Text style={styles.itemTitle}>{'Building notifications'}</Text>
              {BUILDING_NOTIFICATION_CATEGORIES.map((category) => (
                <NotificationSettingRow
                  key={category}
                  title={HappyScoreCategoryTitles[category]}
                  toggled={draftBuilding[category]}
                  toggle={(enabled) => setDraftBuilding((current) => ({
                    ...current,
                    [category]: enabled,
                  }))
                  }
                  testID={`${category.toLowerCase()}-notifications-toggle`}
                  disabled={controlsDisabled}
                />
              ))}
            </>
          )}
          <Text
            style={[
              styles.itemTitle,
              showBuildingNotifications && styles.sectionTitle,
            ]}
          >
            {'Solve ticket notifications'}
          </Text>
          <Text style={styles.channelTitle}>{'Email notifications'}</Text>
          {StaffTicketEmailNotificationEvents.map((eventType) => (
            <NotificationSettingRow
              key={`${NotificationDeliveryChannel.EMAIL}-${eventType}`}
              title={StaffTicketNotificationEventTitles[eventType]}
              toggled={
                draftTicket[NotificationDeliveryChannel.EMAIL][
                  eventType
                ]
              }
              toggle={(enabled) => updateTicketPreference(
                NotificationDeliveryChannel.EMAIL,
                eventType,
                enabled,
              )
              }
              testID={`ticket-email-${eventType.toLowerCase()}-toggle`}
              disabled={controlsDisabled}
            />
          ))}
          <Text style={[styles.channelTitle, styles.channelSpacing]}>
            {'Push notifications'}
          </Text>
          {StaffTicketPushNotificationEvents.map((eventType) => (
            <NotificationSettingRow
              key={`${NotificationDeliveryChannel.PUSH}-${eventType}`}
              title={StaffTicketNotificationEventTitles[eventType]}
              toggled={
                draftTicket[NotificationDeliveryChannel.PUSH][
                  eventType
                ]
              }
              toggle={(enabled) => updateTicketPreference(
                NotificationDeliveryChannel.PUSH,
                eventType,
                enabled,
              )
              }
              testID={`ticket-push-${eventType.toLowerCase()}-toggle`}
              disabled={controlsDisabled}
            />
          ))}
          <Button
            title="Save"
            affirmative
            onPress={save}
            disabled={controlsDisabled}
            children={
              loading && (
                <ActivityIndicator
                  color="#FFF"
                  size="small"
                  animating={loading}
                  style={styles.loadingIndicator}
                />
              )
            }
            style={styles.saveButton}
          />
        </>
      )}
      <AlertModal
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        title={alertTitle}
        message={alertMessage}
        error={alertError}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  itemTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 8,
  },
  sectionTitle: {
    marginTop: 24,
  },
  channelTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginBottom: 8,
  },
  channelSpacing: {
    marginTop: 16,
  },
  loadErrorContainer: {
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  errorText: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
    marginBottom: 8,
  },
  retryButton: {
    alignSelf: 'flex-start',
  },
  saveButton: {
    alignSelf: 'flex-start',
    marginVertical: 32,
  },
  loadingIndicator: {
    marginStart: 12,
  },
});

export default NotificationPreferences;
